import { DefaultButton, Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize, Stack, Text, TextField, mergeStyles } from "@fluentui/react"
import { useEffect, useState } from "react"
import { makeApiCall } from "../services/apiService"

const embeddedSigningFrameClass = mergeStyles({
	width: "100%",
	height: "80vh",
	border: "1px solid #e1dfdd",
	borderRadius: 4
})

const decodeJwtPayload = (token: string): Record<string, any> => {
	const parts = token.split(".")
	if (parts.length !== 3) throw new Error("Invalid signed link token.")
	const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/")
	const padded = payload + "=".repeat((4 - (payload.length % 4 || 4)) % 4)
	const decoded = atob(padded)
	return JSON.parse(decoded)
}

const extractPandaDocIdFromToken = (token: string): string => {
	const payload = decodeJwtPayload(token)
	const id = typeof payload?.pandaDocId === "string" ? payload.pandaDocId.trim() : ""
	if (!id) throw new Error("Invalid signing link. Missing PandaDoc id.")
	return id
}

const extractOpportunityIdFromToken = (token: string): string | undefined => {
	const payload = decodeJwtPayload(token)
	const opportunityId = typeof payload?.opportunityId === "string" ? payload.opportunityId.trim() : ""
	return opportunityId || undefined
}

const isValidEmail = (value: string): boolean => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export function PandaDocESign() {
	const [loading, setLoading] = useState<boolean>(true)
	const [isVerifying, setIsVerifying] = useState<boolean>(false)
	const [isGeneratingReassignLink, setIsGeneratingReassignLink] = useState<boolean>(false)
	const [error, setError] = useState<string>()
	const [reassignError, setReassignError] = useState<string>()
	const [iframeUrl, setIframeUrl] = useState<string>()
	const [encodedId, setEncodedId] = useState<string>()
	const [pandaDocId, setPandaDocId] = useState<string>()
	const [opportunityId, setOpportunityId] = useState<string>()
	const [challengeId, setChallengeId] = useState<string>()
	const [otpCode, setOtpCode] = useState<string>("")
	const [emailHint, setEmailHint] = useState<string>()
	const [otpNotice, setOtpNotice] = useState<string>()
	const [isReassignDialogOpen, setIsReassignDialogOpen] = useState<boolean>(false)
	const [reassignEmail, setReassignEmail] = useState<string>("")
	const [reassignDisplayName, setReassignDisplayName] = useState<string>("")
	const [generatedReassignLink, setGeneratedReassignLink] = useState<string>()
	const [copyNotice, setCopyNotice] = useState<string>()
	const [isSendingGeneratedLinkEmail, setIsSendingGeneratedLinkEmail] = useState<boolean>(false)
	const [sendEmailNotice, setSendEmailNotice] = useState<{ type: MessageBarType; message: string }>()

	const toInvalidSigningLinkMessageIfMatchError = (message: string): string => {
		const normalized = message.toLowerCase()
		if (normalized.includes("does not match signing link recipient") || normalized.includes("status code 403") || normalized.includes("403")) {
			return "This is an invalid signing link."
		}
		return message
	}

	const requestOtp = async (id: string) => {
		const otpResponse = await makeApiCall(`pandadoc/requestEmbeddedSigningOtp?id=${encodeURIComponent(id)}`, "POST")
		if (!otpResponse?.challengeId) throw new Error("Could not initialize email verification.")

		setChallengeId(otpResponse.challengeId)
		setEmailHint(otpResponse.emailHint)
		setOtpNotice("A verification code was sent to your email. Enter it below to continue.")
	}

	const generateReassignLink = async () => {
		if (!pandaDocId) {
			setReassignError("Missing PandaDoc context. Refresh and try again.")
			return
		}

		const nextEmail = reassignEmail.trim().toLowerCase()
		const nextDisplayName = reassignDisplayName.trim()

		if (!nextEmail || !isValidEmail(nextEmail)) {
			setReassignError("Enter a valid email address.")
			return
		}

		try {
			setIsGeneratingReassignLink(true)
			setReassignError(undefined)
			setCopyNotice(undefined)
			setSendEmailNotice(undefined)

			const result = await makeApiCall(
				"pandadoc/generateEmbeddedSigningLinkId",
				"POST",
				JSON.stringify({
					pandaDocId,
					opportunityId,
					email: nextEmail,
					displayName: nextDisplayName || undefined
				})
			)

			if (!result?.id) {
				throw new Error("Failed to generate reassignment link.")
			}

			const reassignedLink = `${window.location.origin}/panda-doc-esign?id=${encodeURIComponent(result.id)}`
			setGeneratedReassignLink(reassignedLink)
			setIframeUrl(undefined)
			setIsReassignDialogOpen(false)
		} catch (err) {
			const message = err instanceof Error ? err.message : "Failed to generate reassignment link."
			setReassignError(message)
		} finally {
			setIsGeneratingReassignLink(false)
		}
	}

	const copyGeneratedLink = async () => {
		if (!generatedReassignLink) return
		try {
			await navigator.clipboard.writeText(generatedReassignLink)
			setCopyNotice("Link copied to clipboard.")
		} catch {
			setCopyNotice("Unable to copy automatically. Copy the link manually.")
		}
	}

	const sendGeneratedLinkByEmail = async () => {
		if (!generatedReassignLink) return

		const email = reassignEmail.trim().toLowerCase()
		if (!email || !isValidEmail(email)) {
			setSendEmailNotice({ type: MessageBarType.error, message: "Enter a valid email address before sending." })
			return
		}

		try {
			setIsSendingGeneratedLinkEmail(true)
			setSendEmailNotice(undefined)

			let companyName = ""
			if (opportunityId) {
				const context = await makeApiCall(`pandadoc/getOpportunityCompanyName?opportunityId=${encodeURIComponent(opportunityId)}`, "GET")
				companyName = ((context?.companyName as string | undefined) || "").trim()
			}

			await makeApiCall(
				"pandadoc/sendEmbeddedSigningLinkEmail",
				"POST",
				JSON.stringify({
					companyName,
					bcc: "",
					recipientName: reassignDisplayName.trim() || undefined,
					recipientEmail: email,
					signerLink: generatedReassignLink,
					pandaDocId
				})
			)
			setSendEmailNotice({ type: MessageBarType.success, message: "Email sent successfully." })
		} catch (err) {
			const message = err instanceof Error ? err.message : "Failed to send email."
			setSendEmailNotice({ type: MessageBarType.error, message })
		} finally {
			setIsSendingGeneratedLinkEmail(false)
		}
	}

	const verifyOtpAndCreateSession = async () => {
		if (!encodedId || !challengeId) {
			setError("Missing verification context. Please refresh and try again.")
			return
		}

		const trimmedCode = otpCode.trim()
		if (!trimmedCode) {
			setError("Enter the verification code from your email.")
			return
		}

		try {
			setIsVerifying(true)
			setError(undefined)
			const session = await makeApiCall(
				`pandadoc/createEmbeddedSigningSessionFromEncodedId?id=${encodeURIComponent(encodedId)}`,
				"POST",
				JSON.stringify({
					challengeId,
					otpCode: trimmedCode,
					lifetime: 90000
				})
			)

			if (!session?.iframeUrl) throw new Error("Failed to create PandaDoc signing session.")
			setIframeUrl(session.iframeUrl)
		} catch (err) {
			const message = err instanceof Error ? err.message : "Failed to verify code."
			setError(toInvalidSigningLinkMessageIfMatchError(message))
		} finally {
			setIsVerifying(false)
		}
	}

	useEffect(() => {
		const run = async () => {
			try {
				setLoading(true)
				setError(undefined)

				const params = new URLSearchParams(window.location.search)
				const id = params.get("id")?.trim()
				if (!id) throw new Error("Missing id query string parameter.")
				setPandaDocId(extractPandaDocIdFromToken(id))
				setOpportunityId(extractOpportunityIdFromToken(id))
				setEncodedId(id)
				await requestOtp(id)
			} catch (err) {
				const message = err instanceof Error ? err.message : "Failed to initialize PandaDoc signing."
				setError(toInvalidSigningLinkMessageIfMatchError(message))
			} finally {
				setLoading(false)
			}
		}

		void run()
	}, [])

	return (
		<Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: 16 } }}>
			{loading && <Spinner size={SpinnerSize.large} label="Preparing PandaDoc signing session..." />}
			{!loading && error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{!loading && !error && generatedReassignLink && (
				<Stack tokens={{ childrenGap: 10 }}>
					<MessageBar messageBarType={MessageBarType.success}>The signing link has been reassigned. Share this new link with the replacement signer.</MessageBar>
					<TextField label="Reassigned signing link" readOnly value={generatedReassignLink} />
					<Stack horizontal tokens={{ childrenGap: 8 }}>
						<PrimaryButton text="Copy link" onClick={() => void copyGeneratedLink()} />
						<DefaultButton text={isSendingGeneratedLinkEmail ? "Sending..." : "Send via Email"} onClick={() => void sendGeneratedLinkByEmail()} disabled={isSendingGeneratedLinkEmail} />
					</Stack>
					{copyNotice && <Text variant="small">{copyNotice}</Text>}
					{sendEmailNotice && <MessageBar messageBarType={sendEmailNotice.type}>{sendEmailNotice.message}</MessageBar>}
				</Stack>
			)}
			{!loading && !error && !generatedReassignLink && !iframeUrl && challengeId && (
				<Stack tokens={{ childrenGap: 10 }}>
					<Text variant="mediumPlus">Email Verification Required</Text>
					{otpNotice && <MessageBar messageBarType={MessageBarType.info}>{otpNotice}</MessageBar>}
					{emailHint && <Text variant="small">Code sent to {emailHint}</Text>}
					<TextField label="Verification code" value={otpCode} onChange={(_, value) => setOtpCode((value || "").replace(/\s+/g, ""))} placeholder="Enter 6-digit code" />
					<PrimaryButton text={isVerifying ? "Verifying..." : "Verify and Continue"} onClick={() => void verifyOtpAndCreateSession()} disabled={isVerifying} />
				</Stack>
			)}
			{!loading && !error && iframeUrl && !generatedReassignLink && (
				<Stack tokens={{ childrenGap: 10 }}>
					<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
						<Text variant="medium">Need someone else to sign?</Text>
						<DefaultButton text="Reassign Signature" onClick={() => setIsReassignDialogOpen(true)} />
					</Stack>
					{reassignError && <MessageBar messageBarType={MessageBarType.error}>{reassignError}</MessageBar>}
					<iframe title="PandaDoc Embedded Signing" src={iframeUrl} className={embeddedSigningFrameClass} allow="clipboard-read; clipboard-write" />
				</Stack>
			)}

			<Dialog
				hidden={!isReassignDialogOpen}
				onDismiss={() => setIsReassignDialogOpen(false)}
				dialogContentProps={{
					type: DialogType.largeHeader,
					title: "Reassign Signature",
					subText: "Enter the replacement signer information to generate a new secure signing link."
				}}>
				<Stack tokens={{ childrenGap: 10 }}>
					<TextField label="Email address" value={reassignEmail} onChange={(_, value) => setReassignEmail(value || "")} required />
					<TextField label="Display name (optional)" value={reassignDisplayName} onChange={(_, value) => setReassignDisplayName(value || "")} />
					{reassignError && <MessageBar messageBarType={MessageBarType.error}>{reassignError}</MessageBar>}
				</Stack>
				<DialogFooter>
					<PrimaryButton text={isGeneratingReassignLink ? "Generating..." : "Generate Link"} onClick={() => void generateReassignLink()} disabled={isGeneratingReassignLink} />
					<DefaultButton text="Cancel" onClick={() => setIsReassignDialogOpen(false)} />
				</DialogFooter>
			</Dialog>
		</Stack>
	)
}
